import React, { useState } from 'react';
import { Sparkles, ChefHat } from 'lucide-react';

interface RecipeCreatorFormProps {
  onSubmit: (prompt: RecipePrompt) => Promise<void>;
  loading: boolean;
  backendStatus: 'checking' | 'online' | 'offline';
}

export interface RecipePrompt {
  title?: string;
  description: string;
  cuisine?: string;
  dietaryRestrictions?: string[];
  servings?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  cookTime?: number;
  ingredients?: string[];
}

const cuisineOptions = [
  'Française', 'Italienne', 'Japonaise', 'Chinoise', 'Mexicaine', 
  'Indienne', 'Thaïlandaise', 'Méditerranéenne', 'Américaine', 'Autre'
];

const dietaryOptions = [
  { value: 'vegetarian', label: 'Végétarien' },
  { value: 'vegan', label: 'Végétalien' },
  { value: 'gluten-free', label: 'Sans gluten' },
  { value: 'dairy-free', label: 'Sans lactose' },
  { value: 'low-carb', label: 'Faible en glucides' },
];

export const RecipeCreatorForm: React.FC<RecipeCreatorFormProps> = ({
  onSubmit,
  loading,
  backendStatus
}) => {
  const [prompt, setPrompt] = useState<RecipePrompt>({
    description: '',
    cuisine: '',
    dietaryRestrictions: [],
    servings: 4,
    difficulty: 'medium',
    cookTime: 30,
    ingredients: []
  });
  
  const [ingredientInput, setIngredientInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.description.trim()) {
      await onSubmit(prompt);
    }
  };

  const handleAddIngredient = () => {
    if (ingredientInput.trim()) {
      setPrompt({
        ...prompt,
        ingredients: [...(prompt.ingredients || []), ingredientInput.trim()]
      });
      setIngredientInput('');
    }
  };

  const handleRemoveIngredient = (index: number) => {
    setPrompt({
      ...prompt,
      ingredients: prompt.ingredients?.filter((_, i) => i !== index)
    });
  };

  const handleDietaryChange = (value: string) => {
    const current = prompt.dietaryRestrictions || [];
    if (current.includes(value)) {
      setPrompt({
        ...prompt,
        dietaryRestrictions: current.filter(d => d !== value)
      });
    } else {
      setPrompt({
        ...prompt,
        dietaryRestrictions: [...current, value]
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Main Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          <ChefHat className="inline h-4 w-4 mr-1" />
          Décrivez la recette que vous souhaitez créer <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          value={prompt.description}
          onChange={(e) => setPrompt({ ...prompt, description: e.target.value })}
          placeholder="Ex: Un plat végétarien avec des pâtes, des légumes de saison et une sauce crémeuse..."
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
          disabled={loading}
        />
        <p className="mt-1 text-sm text-gray-500">
          Soyez aussi précis que possible : ingrédients souhaités, type de plat, saveurs...
        </p>
      </div>

      {/* Optional: Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
          Titre de la recette (optionnel)
        </label>
        <input
          id="title"
          type="text"
          value={prompt.title || ''}
          onChange={(e) => setPrompt({ ...prompt, title: e.target.value })}
          placeholder="Ex: Pâtes crémeuses aux légumes d'automne"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
      </div>

      {/* Two columns: Cuisine & Difficulty */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cuisine" className="block text-sm font-medium text-gray-700 mb-2">
            Type de cuisine
          </label>
          <select
            id="cuisine"
            value={prompt.cuisine || ''}
            onChange={(e) => setPrompt({ ...prompt, cuisine: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="">Peu importe</option>
            {cuisineOptions.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-2">
            Difficulté souhaitée
          </label>
          <select
            id="difficulty"
            value={prompt.difficulty}
            onChange={(e) => setPrompt({ ...prompt, difficulty: e.target.value as any })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="easy">Facile</option>
            <option value="medium">Moyen</option>
            <option value="hard">Difficile</option>
          </select>
        </div>
      </div>

      {/* Two columns: Servings & Cook Time */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="servings" className="block text-sm font-medium text-gray-700 mb-2">
            Nombre de portions
          </label>
          <input
            id="servings"
            type="number"
            value={prompt.servings}
            onChange={(e) => setPrompt({ ...prompt, servings: parseInt(e.target.value) || 4 })}
            min="1"
            max="20"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
        </div>

        <div>
          <label htmlFor="cookTime" className="block text-sm font-medium text-gray-700 mb-2">
            Temps de cuisson max (min)
          </label>
          <input
            id="cookTime"
            type="number"
            value={prompt.cookTime}
            onChange={(e) => setPrompt({ ...prompt, cookTime: parseInt(e.target.value) || 30 })}
            min="5"
            max="300"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
        </div>
      </div>

      {/* Dietary Restrictions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Restrictions alimentaires
        </label>
        <div className="flex flex-wrap gap-2">
          {dietaryOptions.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleDietaryChange(option.value)}
              disabled={loading}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                prompt.dietaryRestrictions?.includes(option.value)
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Specific Ingredients */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ingrédients spécifiques à utiliser (optionnel)
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={ingredientInput}
            onChange={(e) => setIngredientInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIngredient())}
            placeholder="Ex: Tomates, Basilic..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
          <button
            type="button"
            onClick={handleAddIngredient}
            disabled={loading || !ingredientInput.trim()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Ajouter
          </button>
        </div>
        {prompt.ingredients && prompt.ingredients.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {prompt.ingredients.map((ing, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
              >
                {ing}
                <button
                  type="button"
                  onClick={() => handleRemoveIngredient(index)}
                  className="text-green-600 hover:text-green-800"
                  disabled={loading}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {backendStatus === 'offline' && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <span className="text-yellow-800 text-sm">
            ⚠️ Backend offline - Assurez-vous que le serveur backend est démarré
          </span>
        </div>
      )}

      <button
        type="submit"
        disabled={loading || backendStatus !== 'online' || !prompt.description.trim()}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Génération en cours...
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            Créer ma recette avec l'IA
          </>
        )}
      </button>
    </form>
  );
};

